import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { all_routes } from "../router/all_routes";
import useApi from "../../core/customhook/useApi";

const MonthlyFeedbackForm: React.FC = () => {
  const { employee_id } = useParams();

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;

  const fyStart = month >= 4 ? year : year - 1;
  const financialYear = `${fyStart}-${String(fyStart + 1).slice(-2)}`;

  const [feedbackData, setFeedbackData] = useState<any>({});

  const { data: feedbackList, callApi: getFeedbackList } = useApi(
    "/api/monthly-feedback-list-employee-id",
    {
      method: "POST",
      autoCall: false,
    },
  );

  useEffect(() => {
    loadFeedback();
  }, [employee_id]);

  const loadFeedback = async () => {
    try {
      const response = await getFeedbackList({
        employee_id,
        financial_year: financialYear,
      });

      const feedbackRows =
        response?.data || response?.response?.data?.data || [];

      const formattedData: any = {};

      feedbackRows.forEach((item: any) => {
        formattedData[item.feedback_month] = {
          id: item.id,
          feedback: item.feedback,
        };
      });

      setFeedbackData(formattedData);
    } catch (error) {
      console.error(error);
    }
  };

  const months = [
    { id: 1, name: "January" },
    { id: 2, name: "February" },
    { id: 3, name: "March" },
    { id: 4, name: "April" },
    { id: 5, name: "May" },
    { id: 6, name: "June" },
    { id: 7, name: "July" },
    { id: 8, name: "August" },
    { id: 9, name: "September" },
    { id: 10, name: "October" },
    { id: 11, name: "November" },
    { id: 12, name: "December" },
  ];

  const currentMonth = new Date().getMonth() + 1;

  // Show only completed months
  const visibleMonths = months.filter((month) => month.id < currentMonth);

  const handleChange = (monthId: number, value: string) => {
    setFeedbackData((prev: any) => ({
      ...prev,
      [monthId]: {
        ...prev[monthId],
        feedback: value,
      },
    }));
  };

  const { callApi: updateMonthlyFeedback } = useApi("", {
    method: "PUT",
    autoCall: false,
  });

  const { loading: saveLoading, callApi: saveMonthlyFeedback } = useApi(
    "/api/monthly-feedback",
    {
      method: "POST",
      autoCall: false,
    },
  );

  const handleSave = async (monthId: number) => {
    const monthData = feedbackData[monthId];

    const feedback =
      typeof monthData === "string" ? monthData : monthData?.feedback;

    if (!feedback?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please enter feedback.",
      });
      return;
    }

    try {
      let response;

      // UPDATE
      if (monthData?.id) {
        response = await updateMonthlyFeedback(
          {
            feedback,
          },
          `/api/monthly-feedback/${monthData.id}`,
        );
      }

      // ADD
      else {
        response = await saveMonthlyFeedback({
          employee_id,
          financial_year: financialYear,
          feedback_month: monthId,
          feedback,
        });
      }

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text:
            response?.message ||
            (monthData?.id
              ? "Monthly feedback updated successfully."
              : "Monthly feedback added successfully."),
        });

        loadFeedback();
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message || "Failed to save monthly feedback.",
      });
    }
  };
  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Monthly Feedback</h2>

              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.employeeDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>

                  <li className="breadcrumb-item">Monthly Feedback</li>

                  <li className="breadcrumb-item active" aria-current="page">
                    Employee Feedback
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {/* Main Card */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h5 className="mb-1">Employee Monthly Feedback</h5>
                <span className="text-muted">Employee ID : {employee_id}</span>
              </div>

              <div className="d-flex gap-2">
                <select
                  className="form-select"
                  style={{ width: "120px" }}
                  value={financialYear}
                >
                  {(() => {
                    const today = new Date();
                    const year = today.getFullYear();
                    const month = today.getMonth() + 1;

                    const fyStartYear = month >= 4 ? year : year - 1;
                    const fyEndYear = String(fyStartYear + 1).slice(-2);

                    const currentFY = `${fyStartYear}-${fyEndYear}`;

                    return <option value={currentFY}>{currentFY}</option>;
                  })()}
                </select>

                <Link
                  to="/monthly-feedback-team-members"
                  className="btn btn-secondary"
                >
                  Back
                </Link>
              </div>
            </div>

            <div className="card-body">
              <div className="row">
                {visibleMonths.map((month) => (
                  <div className="col-md-6 mb-4" key={month.id}>
                    <div className="card border">
                      <div className="card-header">
                        <h6 className="mb-0">{month.name}</h6>
                      </div>

                      <div className="card-body">
                        <textarea
                          className="form-control"
                          rows={5}
                          placeholder={`Enter ${month.name} feedback`}
                          value={feedbackData[month.id]?.feedback || ""}
                          onChange={(e) =>
                            handleChange(month.id, e.target.value)
                          }
                        />

                        <div className="mt-3 text-end">
                          <button
                            className="btn btn-primary"
                            onClick={() => handleSave(month.id)}
                            disabled={saveLoading}
                          >
                            {saveLoading
                              ? "Saving..."
                              : feedbackData[month.id]?.id
                                ? "Update Feedback"
                                : "Add Feedback"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-flex align-items-center justify-content-center bg-white border-top p-3">
          <p className="mb-0">
            © {new Date().getFullYear()} WeConnect. All rights reserved.
          </p>
        </div>
      </div>
    </>
  );
};

export default MonthlyFeedbackForm;
